import type { Filter } from "@/lib/components/chip-filter/chip-filter";
import type { AssignmentData, AssignmentWorkDays } from "@/models/assignment";
import type { BackwardsCompatibleRolesSelection } from "@/react/prop-types";
import type { TaskModel } from "@bryntum/gantt-thin";
import type { EventSegmentModel } from "@bryntum/schedulerpro-thin";
import type {
   CachedEquipment,
   CachedEquipmentCompanyType,
} from "@laborchart-modules/common/dist/postgres/schemas/common/custom-json";
import type { JobTitle } from "@laborchart-modules/common/dist/postgres/schemas/types/job_titles";
import type { Person } from "@laborchart-modules/common/dist/postgres/schemas/types/people";
import type { Project } from "@laborchart-modules/common/dist/postgres/schemas/types/projects";
import type { TimeOff } from "@laborchart-modules/common/dist/postgres/schemas/types/time_off";

export type GanttContainerPropTypes = {
   groupId: string;
};

type RequestData = {
   id: string;
   end_day: string;
   tag_ids: string[];
   archived: boolean;
   comments: string[];
   end_time: string;
   label_id: string | null;
   start_day: string;
   status_id: string | null;
   work_days: AssignmentWorkDays;
   company_id: string;
   created_at: string;
   creator_id: string | null;
   editor_ids: string[];
   project_id: string;
   start_time: string;
   archived_at: string | null;
   cost_code_id: string;
   job_title_id: string | null;
   custom_fields: any[];
   work_scope_text: string | null;
   instruction_text: string | null;
   percent_allocated: null | number;
};

export type EquipmentAssignmentData = {
   id: string;
   status: string | null;
   end_day: string;
   end_time: string;
   label_id: string | null;
   overtime: boolean;
   pay_split: any | null;
   start_day: string;
   status_id: string | null;
   work_days: AssignmentWorkDays;
   start_time: string;
   resource_id: string;
   cost_code_id: string | null;
   overtime_rates: any | null;
   percent_allocated: number | null;
   cost_code: {
      id: string;
      name: string;
      labels: Array<{
         id: string;
         name: string;
         archived: boolean;
         sequence: number;
      }>;
      archived: boolean;
      sequence: number;
   } | null;
   label: {
      id: string;
      name: string;
      archived: boolean;
      sequence: number;
   } | null;
};

export type EquipmentRequestData = {
   id: string;
   status: string | null;
   end_day: string;
   end_time: string;
   start_day: string;
   status_id: string | null;
   start_time: string;
   type_id: string;
   percent_allocated: number | null;
   work_days: AssignmentWorkDays;
   cost_code_id: string | null;
   cost_code: {
      id: string;
      name: string;
      labels: Array<{
         id: string;
         name: string;
         archived: boolean;
         sequence: number;
      }>;
      archived: boolean;
      sequence: number;
   } | null;
   label_id: string | null;
   label: {
      id: string;
      name: string;
      archived: boolean;
      sequence: number;
   } | null;
};

export type GanttProject = Omit<Project, "start_date" | "est_end_date"> & {
   start_date: string;
   est_end_date: string;
   assignments: AssignmentData[];
   requests: RequestData[];
   equipment_assignments: EquipmentAssignmentData[];
   equipment_requests: EquipmentRequestData[];
   tbd_weeks: number;
};

export type GanttOptions = {
   skip: number;
   limit?: number;
   projectSort: string;
   group_id: string;
   startDay: number;
   endDay: number;
   filters?: {
      [key: string]: Filter[];
   };
   search?: string;
   timezone: string;
   sortDirection?: "asc" | "desc";
   isLegacyGantt?: boolean;
};

export type RawGanttData = {
   jobTitles: JobTitle[];
   people: Array<Person & { time_off: TimeOff[]; job_title_id: string }>;
   equipment: CachedEquipment[];
   equipmentTypes: CachedEquipmentCompanyType[];
   projects: GanttProject[];
};

export type Category = {
   id: string;
   name: string;
   labels: Subcategory[];
   sequence: number;
   archived: boolean;
};

export type Subcategory = {
   id: string;
   name: string;
   archived: boolean;
   sequence: number;
};

export type GroupableTask = {
   cost_code_id: string;
   label_id: string;
};

export enum GanttViewMode {
   PROJECTS = "projects",
   RESOURCES = "resources",
}

export enum TaskType {
   ASSIGNMENT = "assignment",
   EQUIPMENT_ASSIGNMENT = "equipment_assignment",
   REQUEST = "request",
   EQUIPMENT_REQUEST = "equipment_request",
   PROJECT = "project",
   CATEGORY = "category",
   SUBCATEGORY = "subcategory",
}

export type Task = Partial<TaskModel> & {
   type: TaskType;
   calendar?: string;
   projectId: string;
   projectName: string;
   projectColor: string;
   projectJobNumber: string;
   jobTitleId?: string;
   jobTitleName?: string;
   jobTitleColor?: string;
   typeId?: string;
   equipmentTypeName?: string;
   equipmentTypeColor?: string;
   parentCategoryId?: string;
   parentCategoryName?: string;
   parentSubcategoryId?: string;
   parentSubcategoryName?: string;
   startTime?: string;
   endTime?: string;
   workDays: AssignmentWorkDays;
   sortPriority: number;
   timeOff?: TimeOff[];
   categoryId: string | null;
   subcategoryId: string | null;
   resourceId?: string;
   resourceName?: string;
   segments?: EventSegmentModel[];
   overtime?: boolean;
   status?: {
      id: string;
      name: string;
      color: string | null;
   };
   percentAllocated?: number | null;
   instructionText?: string | null;
   workScopeText?: string | null;
   tagIds?: string[];
   employeeNumber?: string | null;
   phoneNumber?: string | null;
   resourceAddress1?: string | null;
   resourceAddress2?: string | null;
   resourceCityTown?: string | null;
   resourceHourlyWage?: number | null;
   resourceStateProvince?: string | null;
   resourceZipcode?: string | null;
};

export type GroupedTasks = Array<
   | {
        name: string; // category
        children: Array<{
           name: string; // subcategory
           children: Task[]; // assignments or requests
        }>;
     }
   | Task
>;

export type TaskLookup = {
   [categoryId: string]: {
      [subcategoryId: string]: Task[];
   };
};

export type noSubcategoryTasks = {
   [categoryId: string]: Task[];
};

export type projectStatus = {
   id: number;
   name: string;
};

export enum FilterTaskType {
   ASSIGNMENTS = "assignments",
   REQUESTS = "requests",
}

export enum FilterResourceType {
   LABOR = "labor",
   EQUIPMENT = "equipment",
}

export enum dateQualifier {
   ONORBEFORE = "<=",
   ONORAFTER = ">=",
   ON = "=",
   AFTER = ">",
   BEFORE = "<",
}

export const ASSIGNMENT_TASK_TYPES = new Set([TaskType.ASSIGNMENT, TaskType.EQUIPMENT_ASSIGNMENT]);
export const REQUEST_TASK_TYPES = new Set([TaskType.REQUEST, TaskType.EQUIPMENT_REQUEST]);
export const REQUEST_AND_ASSIGNMENT_TASK_TYPES = new Set([
   ...ASSIGNMENT_TASK_TYPES.values(),
   ...REQUEST_TASK_TYPES.values(),
]);
export const EQUIPMENT_TASK_TYPES = new Set([
   TaskType.EQUIPMENT_ASSIGNMENT,
   TaskType.EQUIPMENT_REQUEST,
]);

export type ganttFilterType = {
   jobTitles: Array<{ id: string; name: string }>;
   projectStatuses: projectStatus[];
   resourceStatuses: projectStatus[];
   assignmentsOrRequests: FilterTaskType[];
   peopleOrEquipment: FilterResourceType[];
   hidePast: boolean;
   hideEmptyProject: boolean;
   hideEmptyCategories: boolean;
   hideTypeLabor: boolean;
   hideTypeEquipment: boolean;
   hideAssignments: boolean;
   hideRequests: boolean;
   projectStages: Array<{ id: string; name: string }>;
   projectRoles: BackwardsCompatibleRolesSelection; // We'll plan for both values to support backwards compatibility
   customFieldsFilters: { [key: string]: any };
   projectTags: Array<{ id: string; name: string }>;
   projects: Array<{ id: string; name: string }>;
};

export type ganttFilterOptionTypes =
   | "jobTitles"
   | "projectStatuses"
   | "resourceStatuses"
   | "assignmentsOrRequests"
   | "peopleOrEquipment"
   | "hidePast"
   | "hideEmptyProject"
   | "hideEmptyCategories"
   | "hideTypeLabor"
   | "hideTypeEquipment"
   | "hideAssignments"
   | "hideRequests"
   | "projectStages"
   | "projectTags"
   | "projectRoles"
   | "projects";

export enum ganttFilterTypeEnums {
   jobTitles = "jobTitles",
   projectStatuses = "projectStatuses",
   resourceStatuses = "resourceStatuses",
   onlyShow = "onlyShow",
   hidePast = "hidePast",
   hideEmptyProject = "hideEmptyProject",
   customFieldsFilters = "customFieldsFilters",
   hideTypeLabor = "hideTypeLabor",
   hideTypeEquipment = "hideTypeEquipment",
   hideAssignments = "hideAssignments",
   hideRequests = "hideRequests",
   projectTags = "projectTags",
   projectStages = "projectStages",
   projectRoles = "projectRoles",
}

export enum ganttFilterPillType {
   MULTISELECT = "multiselect",
   BOOLEAN = "boolean",
   CHECKBOX = "checkbox",
   DATE = "date",
}

export enum TotalUnitType {
   PEOPLE = "people",
   COST = "cost",
   HOURS = "hours",
   MAN_DAYS = "manDays",
}

export enum AssignmentBarColors {
   PROJECT = "Project",
   ALLOCATION_PERCENTAGE = "Allocation Percentage",
   JOB_TITLES = "Job Titles",
   OVERTIME = "Overtime",
   STATUS = "Status",
}

export type TotalByDay = {
   date: Date;
   hours?: number;
   manDays?: number;
   cost?: number;
   people?: number;
};

export type GroupedTasksWrapper = {
   tasks: GroupedTasks;
   earliestTaskStartDate: null | Date;
   latestTaskEndDate: null | Date;
};

export type GanttCustomFieldFilter = {
   property: string;
   type: string;
   negation: boolean;
   filterName: string | number;
   customFieldId: string | number;
   classifier?: any;
   value?: any;
};

export type GanttTooltipComponentProps = {
   dateRangeString: string;
   name: string;
   type: TaskType;
   projectName: string;
   projectColor?: string;
   projectJobNumber?: string;
   categoryName?: string;
   jobTitleColor?: string;
   jobTitleName?: string;
   startTime?: string | null;
   endTime?: string | null;
   isTaskWithTime?: boolean;
   isRequest?: boolean;
   isEquipment?: boolean;
   taskName?: string;
   taskTimeAllocation?: string;
   instructionText?: string | null;
   workScopeText?: string | null;
   workingDayClass?: any;
   paidShiftHours: number;
   onClickResourceName?: () => void;
};
